/**
 * レビューの経緯（片付いた指摘と、そこへ至る出来事）。
 *
 * **画面は未解決の指摘しか出していなかった。** 直した・見送った指摘は節から消え、理由も
 * 読めないので、「誰が何を言って、どう決着したか」を画面で追えない。レビューを読む人が
 * Slack を探しに行く原因がここにある。
 *
 * 片付いた指摘は案件の詳細が返す（`resolvedFindings`）。誰がいつ記録したかはレビュー本体に
 * 残らない（操作のたびに上書きされる）ので、**開いたときだけ**出来事の記録を引く。
 * 案件は成果物 6 つぶんのレビューを持つため、開いた時点で全部引くと読まれない取得が 6 回走る。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 要件定義のレビューで片付いた指摘とその理由・誰がいつ記録したかを画面で読めるようにする
 * @graph-connects useSpecReviewEvents [calls] レビューの出来事の取得
 * @graph-connects FoundByBadge [calls] 指摘の出自の印
 * @graph-connects ReviewReply [calls] 成果物ごとの会話の入力欄
 */

import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useSpecReviewEvents } from '../../../hooks/useSpecRequirements.js';
import { formatLocalYmdHm } from '../../../lib/date-utils.js';
import type { SpecResolvedFinding, SpecReviewEvent } from '../../../lib/api/spec-requirements.js';
import { ReviewReply } from './ReviewReply.js';
import {
  FoundByBadge,
  SeverityBadge,
  actorLabel,
  perspectiveLabel,
  resolutionLabel,
} from './finding-display.js';

/**
 * 出来事の種類の翻訳キー。提出は役で分ける（AI レビューと DLE レビューは別の出来事として読む）。
 * @graph-connects none
 */
const EVENT_KEY: Record<string, string> = {
  requested: 'spec.review.event.requested',
  'submitted:ai': 'spec.review.event.submittedAi',
  'submitted:dle': 'spec.review.event.submittedDle',
  resolved: 'spec.review.event.resolved',
  dle_approval_revoked: 'spec.review.event.revoked',
  closed: 'spec.review.event.closed',
  commented: 'spec.review.event.commented',
};

/**
 * 出来事の呼び名。知らない種類は API の返した値をそのまま出す（訳せない値を隠さない）。
 * @graph-connects none
 */
function eventLabel(t: TFunction, event: SpecReviewEvent): string {
  const key = EVENT_KEY[`${event.kind}:${event.role ?? ''}`] ?? EVENT_KEY[event.kind];
  return key === undefined ? event.kind : t(key);
}

export interface ReviewHistoryProps {
  /** 案件キー。出来事の取得に使う */
  pjKey: string;
  /** レビュー対象（業務フローは flow_id、案件に 1 つの成果物は成果物名） */
  targetId: string;
  /** 対象の呼び名。同じ文言のボタンが節ごとに並ぶので、読み上げで区別するために使う */
  label: string;
  /**
   * 片付いた指摘。**API が古い間は来ない**ので、来ないときは節を出さない
   * （空配列として扱うと「片付いた指摘は無い」に見える）。
   */
  resolvedFindings?: SpecResolvedFinding[];
  /** email → ニックネームの対応。出した人・記録した人の表示に使う */
  nicknameByEmail: Map<string, string>;
  /**
   * 開いているか。**自前で持たない。**
   *
   * 返信は経緯の中に出来事として並ぶので、画面から返信を書いたら書いた本人に見えていないと
   * 「送れたのか」が分からない。開閉を上（`ReviewActions`）が持てば、返信が通った時点で
   * 開ける。自前で持つと上から開けられず、送れたことを文章で言い足すことになる。
   */
  open: boolean;
  /** 見る／閉じるを押したとき */
  onToggle: () => void;
}

/**
 * 片付いた指摘 1 件。扱いと理由を指摘の下に出す。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 片付いた指摘を、出自・重さ・観点・扱い・理由つきで 1 行として出す
 * @graph-connects FoundByBadge [calls] 指摘の出自の印
 */
function ResolvedFinding({
  finding,
  nicknameByEmail,
}: {
  finding: SpecResolvedFinding;
  nicknameByEmail: Map<string, string>;
}) {
  const { t } = useTranslation();
  return (
    <li className="border border-[var(--color-border)] rounded p-3">
      <div className="flex items-center gap-2 flex-wrap">
        <SeverityBadge severity={finding.severity} />
        <FoundByBadge finding={finding} nicknameByEmail={nicknameByEmail} />
        <span className="text-xs text-[var(--color-text-muted)]">
          {perspectiveLabel(t, finding.perspective)}
          {finding.target === '' ? '' : ` ・ ${finding.target}`}
        </span>
        <span className="text-xs text-[var(--color-text-secondary)]">
          {resolutionLabel(t, finding.resolution)}
        </span>
        <span className="text-xs text-[var(--color-text-muted)] ml-auto font-mono">
          {finding.id}
        </span>
      </div>
      <p className="mt-2 text-sm">{finding.summary}</p>
      {finding.resolutionNote === '' ? null : (
        <p className="mt-1 text-sm text-[var(--color-text-secondary)] whitespace-pre-wrap">
          {finding.resolutionNote}
        </p>
      )}
    </li>
  );
}

/**
 * 出来事 1 件。誰がいつ何をしたかと、その回に扱いが変わった指摘の ID を出す。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business レビューの依頼・提出・解決の 1 回ぶんを、誰がいつ行ったかと合わせて出す
 * @graph-connects actorLabel [calls] 操作した人の呼び名
 */
function ReviewEventRow({
  event,
  nicknameByEmail,
}: {
  event: SpecReviewEvent;
  nicknameByEmail: Map<string, string>;
}) {
  const { t } = useTranslation();
  const who = actorLabel(t, event.actor, nicknameByEmail);
  const replyBody = event.kind === 'commented' ? (event.body ?? '') : '';
  return (
    <li className="flex gap-2 flex-wrap text-xs text-[var(--color-text-secondary)]">
      <span className="font-mono text-[var(--color-text-muted)]">{formatLocalYmdHm(event.at)}</span>
      <span>{eventLabel(t, event)}</span>
      {who === '' ? null : <span className="text-[var(--color-text-muted)]">{who}</span>}
      <span className="text-[var(--color-text-muted)]">v{event.targetVersion}</span>
      {event.changedFindingIds.length === 0 ? null : (
        <span className="font-mono text-[var(--color-text-muted)]">
          {event.changedFindingIds.join(' / ')}
        </span>
      )}
      {event.kind === 'commented' && event.findingId ? (
        <span className="font-mono text-[var(--color-text-muted)]">
          {t('spec.review.event.commentedOn', { id: event.findingId })}
        </span>
      ) : null}
      {replyBody === '' ? null : (
        <p className="basis-full whitespace-pre-wrap text-sm text-[var(--color-text-secondary)]">
          {replyBody}
        </p>
      )}
    </li>
  );
}

/**
 * 経緯の節。開いたときに出来事を引く（畳んでいる間は引かない）。
 *
 * **片付いた指摘が 0 件でも出す。** 出来事の記録には「何回依頼し直したか」「中身が変わって
 * 捨てた指摘」が残っており、指摘が残っていない成果物でも読む値がある。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 要件定義のレビューの経緯（片付いた指摘と出来事）を、開いたときだけ読み込んで出し、末尾に会話の入力欄を置く
 * @graph-connects useSpecReviewEvents [calls] レビューの出来事の取得
 * @graph-connects ReviewReply [calls] 成果物ごとの会話の入力欄
 */
export function ReviewHistory({
  pjKey,
  targetId,
  label,
  resolvedFindings,
  nicknameByEmail,
  open,
  onToggle,
}: ReviewHistoryProps) {
  const { t } = useTranslation();
  const events = useSpecReviewEvents(pjKey, targetId, open);
  const resolved = resolvedFindings ?? [];

  return (
    <div className="mt-4">
      <button
        type="button"
        className="text-xs px-3 py-1 border border-[var(--color-border)] rounded bg-[var(--color-bg-elevated)] cursor-pointer"
        aria-expanded={open}
        aria-label={t(open ? 'spec.review.historyHideAria' : 'spec.review.historyShowAria', {
          label,
        })}
        onClick={onToggle}
      >
        {open ? t('spec.review.historyHide') : t('spec.review.historyShow', { n: resolved.length })}
      </button>

      {!open ? null : (
        <div className="mt-3">
          {resolved.length === 0 ? null : (
            <ul className="space-y-3">
              {resolved.map((finding) => (
                <ResolvedFinding
                  key={finding.id}
                  finding={finding}
                  nicknameByEmail={nicknameByEmail}
                />
              ))}
            </ul>
          )}

          {events.isPending ? (
            <p role="status" className="mt-3 text-xs text-[var(--color-text-muted)]">
              {t('spec.review.historyLoading')}
            </p>
          ) : null}
          {events.error === null ? null : (
            <p role="alert" className="mt-3 text-xs text-[var(--color-error)]">
              {t('spec.review.historyFailed')}
            </p>
          )}
          {events.data === undefined || events.data.events.length === 0 ? null : (
            <ul className="mt-3 space-y-1 border-t border-[var(--color-border)] pt-3">
              {events.data.events.map((event) => (
                <ReviewEventRow key={event.seq} event={event} nicknameByEmail={nicknameByEmail} />
              ))}
            </ul>
          )}
          {events.data !== undefined && events.data.events.length === 0 && resolved.length === 0 ? (
            <p className="mt-3 text-xs text-[var(--color-text-muted)]">
              {t('spec.review.historyEmpty')}
            </p>
          ) : null}
          {/* どの指摘にも紐づかない会話は、読んだ流れの続きとしてここに置く */}
          <ReviewReply pjKey={pjKey} targetId={targetId} findingId={null} label={label} />
        </div>
      )}
    </div>
  );
}
